import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api-client';
import type { DocumentItem, DocumentsListResponse } from './types';

interface CreateDocumentInput {
  type: string;
  description?: string;
  issueDate?: string;
  expiresAt?: string;
  uploadId: string;
}

// -----------------------------------------------------------------------------
// Vehicle Documents
// -----------------------------------------------------------------------------

const VK = {
  list: (vehicleId: string) => ['vehicles', vehicleId, 'documents'] as const,
};

export function useVehicleDocuments(vehicleId: string, enabled = true) {
  return useQuery<DocumentsListResponse>({
    queryKey: VK.list(vehicleId),
    enabled: enabled && !!vehicleId,
    queryFn: async () => {
      const { data } = await api.get<DocumentsListResponse>(
        `/vehicles/${vehicleId}/documents`,
      );
      return data;
    },
  });
}

export function useCreateVehicleDocument(vehicleId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateDocumentInput) => {
      const { data } = await api.post<{ data: DocumentItem }>(
        `/vehicles/${vehicleId}/documents`,
        input,
      );
      return data.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: VK.list(vehicleId) });
    },
  });
}

export function useDeleteVehicleDocument(vehicleId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (docId: string) => {
      await api.delete(`/vehicles/${vehicleId}/documents/${docId}`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: VK.list(vehicleId) });
    },
  });
}

// -----------------------------------------------------------------------------
// Driver Documents
// -----------------------------------------------------------------------------

const DK = {
  list: (driverId: string) => ['drivers', driverId, 'documents'] as const,
};

export function useDriverDocuments(driverId: string, enabled = true) {
  return useQuery<DocumentsListResponse>({
    queryKey: DK.list(driverId),
    enabled: enabled && !!driverId,
    queryFn: async () => {
      const { data } = await api.get<DocumentsListResponse>(
        `/drivers/${driverId}/documents`,
      );
      return data;
    },
  });
}

export function useCreateDriverDocument(driverId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateDocumentInput) => {
      const { data } = await api.post<{ data: DocumentItem }>(
        `/drivers/${driverId}/documents`,
        input,
      );
      return data.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: DK.list(driverId) });
    },
  });
}

export function useDeleteDriverDocument(driverId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (docId: string) => {
      await api.delete(`/drivers/${driverId}/documents/${docId}`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: DK.list(driverId) });
    },
  });
}

// -----------------------------------------------------------------------------
// Download (URL presigned)
// -----------------------------------------------------------------------------

export async function getUploadDownloadUrl(uploadId: string): Promise<string> {
  const { data } = await api.get<{ data: { url: string; expiresAt: string } }>(
    `/uploads/${uploadId}/download`,
  );
  return data.data.url;
}
